/*
 *  TTTech nerve-management-system
 *  Copyright(c) 2021. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import axios from 'axios';
import { NodesApiService, WorkloadsApiService } from '@/services/api';
import mqtt from '@/plugins/mqtt';
import i18n from '@/i18n';
import useNodesStore from '@/store/modules/pinia/nodes';

class NodeTreeHelper {
  // field used to place exchange interval timer instance
  exchangeInterval;

  // exchange interval length
  EXCHANGE_INTERVAL_LENGTH = 10000;

  // field used to set selected node from tree
  selectedNode;

  // flag for checking if startEmittingUpdates event triggered properly
  isTriggerStartEmitting = false;

  /**
   * @description setting timer to command node to continue
   * triggering on every 10 seconds
   * @type {function(): (*)}
   */
  async invokeMqttEvents() {
    try {
      // Clear any existing interval before creating a new one to prevent multiple intervals
      if (this.exchangeInterval) {
        clearInterval(this.exchangeInterval);
        this.exchangeInterval = undefined;
      }

      this.exchangeInterval = setInterval(() => {
        if (!this.selectedNode || !this.selectedNode?.isOnline() || !this.isTriggerStartEmitting) {
          return;
        }
        NodesApiService.keepEmittingUpdates({
          dataId: 'system_stats',
          serialNumber: this.selectedNode.serialNumber,
        }).catch((e) => {
          console.debug('NodesApiService.keepEmittingUpdates ', e.message);
          this.clearExchangeInterval();
          mqtt.unsubscribeFrom('node', this.selectedNode);
        });
      }, this.EXCHANGE_INTERVAL_LENGTH);
    } catch (e) {
      console.debug('NodesApiService.invokeMqttEvents ', e.message);
    }
  }

  /**
   * @description sending request that will trigger node to start sending mqtt events
   * @type {function(): (*)}
   */
  async startEmitting({ newSelectedDevice }) {
    const nodesStore = useNodesStore();
    try {
      if (!newSelectedDevice.isOnline()) {
        return;
      }

      const source = axios.CancelToken.source();
      nodesStore.setCancelTokenStartSending(source);
      await NodesApiService.startEmittingUpdates({
        dataId: 'system_stats',
        serialNumber: newSelectedDevice.serialNumber,
        cancelToken: source.token,
      });
      this.isTriggerStartEmitting = true;
    } catch (e) {
      this.isTriggerStartEmitting = false;
      console.debug('NodesApiService.startEmittingUpdates ', e.message);
    }
  }

  /**
   * @description Set selected node from tree
   * @type {function(): (*)}
   */
  setSelectedNode(node) {
    this.selectedNode = node;
  }

  /**
   * @description Set StartEmitting flag
   * @type {function(): (*)}
   */
  setStartEmittingFlag(value) {
    this.isTriggerStartEmitting = value;
  }

  /**
   * @description Function for canceling pending requests
   * @param token - cancelation token for axios request
   */

  async cancelPendingRequests(token) {
    if (token.cancel) {
      await token.cancel(i18n.global.t('nodes.tree.cancelRequestMessage.nodeRequests'));
    }
  }

  async cancelPendingDnaStatusRequests(token) {
    if (token.cancel) {
      await token.cancel(i18n.global.t('nodes.tree.cancelRequestMessage.dnaStatus'));
    }
  }

  /**
   * @description If node is online, send requests to get additional information
   * about the selected node, cached data and details
   * @param selectedDevice - selected tree node's device
   * @param workloadControlPermission
   * @returns {Promise<(*&{devices: *})|*>}
   */

  async fetchNodeDetails(selectedDevice, workloadControlPermission) {
    let fetchedNode = null;
    let ip_address = '';
    const nodesStore = useNodesStore();
    try {
      if (!selectedDevice.isOnline()) {
        return selectedDevice;
      }

      let source = axios.CancelToken.source();
      nodesStore.setCancelToken(source);
      fetchedNode = await NodesApiService.getNodeById(selectedDevice.id, { fetchFreshNodeData: true }, source.token);

      source = axios.CancelToken.source();
      nodesStore.setCancelToken(source);
      const cachedData = await NodesApiService.getCachedData({
        dataId: 'wan_ip_address',
        serialNumber: selectedDevice.serialNumber,
        fromNodeIfCacheEmpty: true,
        cancelToken: source.token,
      });
      ip_address = cachedData.values;
      if (workloadControlPermission) {
        source = axios.CancelToken.source();
        nodesStore.setCancelToken(source);
        const devices = await WorkloadsApiService.getAllDeployedWorkloads({
          serialNumber: selectedDevice.serialNumber,
          cancelToken: source.token,
        });
        return {
          ...selectedDevice,
          ...fetchedNode,
          ...ip_address,
          devices,
        };
      }
      return { ...selectedDevice, ...fetchedNode, ...ip_address };
    } catch (e) {
      console.debug(e);
      return {
        ...selectedDevice,
        ...fetchedNode,
        ...ip_address,
        cancelOcurred: e.message === i18n.global.t('nodes.tree.cancelRequestMessage.nodeRequests'),
      };
    }
  }

  /**
   * @description Clear exchange interval timer
   */
  clearExchangeInterval() {
    clearInterval(this.exchangeInterval);
    this.exchangeInterval = undefined;
  }

  /**
   * @description Add or update and merge with existing deployed workload
   * workload received trough mqtt or api endpoint
   * @param workloads
   * @param treeNodeDevice
   * @returns {*[]}
   */

  addOrUpdateAndMergeDeployedWls(workloads, treeNodeDevice) {
    const addedWorkloads = [];
    workloads.forEach((wl) => {
      treeNodeDevice.devices.forEach((device) => {
        if (device.id === wl.deviceId) {
          // to avoid that id property from device which is deviceId
          // is overwritten by workload id which is workloadId
          delete wl.id;
          Object.assign(device, wl);
        }
      });
      if (!treeNodeDevice.devices.map((d) => d.deviceId).includes(wl.deviceId)) {
        addedWorkloads.push(wl);
      }
    });
    return [...treeNodeDevice.devices, ...addedWorkloads];
  }
}

export default new NodeTreeHelper();
